import React from "react"

import type { Organization } from "@/models/organization"
import { useUserState } from "@/utils/use-user-state"

interface ActiveOrganizationContextValue {
  activeOrganization: Organization | null | undefined
  setActiveOrganization: (organization: Organization | null) => void
}

interface ActiveOrganizationProviderProps extends Partial<ActiveOrganizationContextValue> {
  children: React.ReactNode
}

const ActiveOrganizationContext =
  React.createContext<ActiveOrganizationContextValue>({
    activeOrganization: undefined,
    setActiveOrganization: () => {
      console.error("setActiveOrganization not implemented")
    },
  })

/**
 * Provides the active organization context
 *
 * @example
 * return (
 *   <ActiveOrganizationProvider>
 *     <Component />
 *   </ActiveOrganizationProvider>
 * )
 */
export const ActiveOrganizationProvider = (
  props: ActiveOrganizationProviderProps
) => {
  const { saveActiveOrganization } = useUserState()

  const [activeOrganization, setActiveOrganization] = React.useState(
    props.activeOrganization
  )

  // Public organization setter to handle additional behavior
  const setActiveOrganizationPublic = React.useCallback(
    (org: Organization | null): void => {
      setActiveOrganization(org)
      saveActiveOrganization(org?.id)
    },
    [saveActiveOrganization]
  )

  const value: ActiveOrganizationContextValue = React.useMemo(
    () => ({
      activeOrganization,
      setActiveOrganization: setActiveOrganizationPublic,
    }),
    [activeOrganization, setActiveOrganizationPublic]
  )

  return (
    <ActiveOrganizationContext value={value}>
      {props.children}
    </ActiveOrganizationContext>
  )
}

/**
 * Provides the active organization getter and setter
 *
 * @example
 * const { activeOrganization, setActiveOrganization } = useActiveOrganization()
 */
export const useActiveOrganization = () => React.use(ActiveOrganizationContext)
